import { useLayoutEffect, type RefObject } from "react";

/**
 * The issue title's textarea, as the detail panel draws it: borderless until
 * hovered or focused, semibold, its text aligned with the labels below. Shared
 * with the New issue draft so the two read as the same field.
 */
export const ISSUE_TITLE_TEXTAREA_CLASS =
  "-ml-1 block w-[calc(100%+0.5rem)] resize-none break-words rounded-md border border-transparent bg-transparent px-1 py-0 font-semibold text-foreground shadow-none outline-none transition-colors hover:border-input focus-visible:border-input focus-visible:ring-1 focus-visible:ring-ring";

const FONT_STEPS = [20, 18, 16, 14];
const LINE_HEIGHT = 1.3;
const MAX_LINES = 4;

/**
 * Auto-fit the title: pick the largest font size (20→18→16→14px) at which the
 * title wraps within MAX_LINES, then size the box to its content. Past the floor
 * (14px) and MAX_LINES the box stops growing and scrolls. Re-fits when `value`
 * changes and when the host (a resizable panel, a dialog) changes the
 * textarea's width.
 */
export function useAutoFitTitle(ref: RefObject<HTMLTextAreaElement>, value: string): void {
  useLayoutEffect(() => {
    const ta = ref.current;
    if (!ta) return;
    const fit = () => {
      ta.style.lineHeight = String(LINE_HEIGHT);
      for (const size of FONT_STEPS) {
        ta.style.fontSize = `${size}px`;
        ta.style.height = "auto";
        const maxH = size * LINE_HEIGHT * MAX_LINES;
        const isFloor = size === FONT_STEPS[FONT_STEPS.length - 1];
        if (ta.scrollHeight <= maxH + 1 || isFloor) {
          ta.style.height = `${Math.min(ta.scrollHeight, maxH)}px`;
          ta.style.overflowY = ta.scrollHeight > maxH + 1 ? "auto" : "hidden";
          break;
        }
      }
    };
    fit();
    // Re-fit only on width changes — guarding on width avoids a feedback loop
    // from our own height mutations retriggering the observer.
    let lastWidth = ta.clientWidth;
    const ro = new ResizeObserver(() => {
      if (ta.clientWidth !== lastWidth) {
        lastWidth = ta.clientWidth;
        fit();
      }
    });
    ro.observe(ta);
    return () => ro.disconnect();
  }, [ref, value]);
}
